/**
 * Version Check Utility
 * Gestisce il rilevamento di nuove versioni e il reload automatico
 * CON PROTEZIONE LOOP INFINITO
 */
import { setUpdateOverlayState } from './update-overlay-state';

const VERSION_CHECK_INTERVAL = 30000; // 30 secondi
const VERSION_KEY = 'app_build_version';
const LAST_CHECK_KEY = 'app_version_last_check';
const RELOAD_COUNT_KEY = 'app_reload_count';
const RELOAD_TIMESTAMP_KEY = 'app_reload_timestamp';
const MAX_RELOAD_ATTEMPTS = 3; // Massimo 3 tentativi
const RELOAD_WINDOW_MS = 60000; // In 60 secondi

interface VersionInfo {
  version: string;
  buildTime: string;
  deployTime: string;
}

/**
 * Detect user language from Telegram or browser
 */
export function getUserLanguage(): string {
  try {
    const tgLang = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    if (tgLang) return tgLang.substring(0, 2);
  } catch (_) { /* ignore */ }
  try {
    return navigator.language.substring(0, 2);
  } catch (_) { /* ignore */ }
  return 'en';
}

/**
 * i18n strings for version check screens
 */
function t(key: string): string {
  const lang = getUserLanguage();
  const strings: Record<string, Record<string, string>> = {
    updating: {
      it: 'Aggiornamento in corso',
      en: 'Updating',
      es: 'Actualizando',
      de: 'Aktualisierung',
      fr: 'Mise à jour en cours',
      pt: 'Atualizando',
    },
    updatingDesc: {
      it: 'Stiamo caricando la nuova versione',
      en: 'Loading the new version',
      es: 'Cargando la nueva versión',
      de: 'Neue Version wird geladen',
      fr: 'Chargement de la nouvelle version',
      pt: 'Carregando a nova versão',
    },
    newVersion: {
      it: 'Nuova versione disponibile!',
      en: 'New version available!',
      es: '¡Nueva versión disponible!',
      de: 'Neue Version verfügbar!',
      fr: 'Nouvelle version disponible !',
      pt: 'Nova versão disponível!',
    },
    newVersionDesc: {
      it: 'Aggiorna per usufruire delle ultime migliorie',
      en: 'Update to enjoy the latest improvements',
      es: 'Actualiza para disfrutar de las últimas mejoras',
      de: 'Aktualisiere für die neuesten Verbesserungen',
      fr: 'Mettez à jour pour profiter des améliorations',
      pt: 'Atualize para aproveitar as últimas melhorias',
    },
    updateNow: {
      it: 'Aggiorna ora',
      en: 'Update now',
      es: 'Actualizar ahora',
      de: 'Jetzt aktualisieren',
      fr: 'Mettre à jour',
      pt: 'Atualizar agora',
    },
    updateLater: {
      it: 'Dopo',
      en: 'Later',
      es: 'Después',
      de: 'Später',
      fr: 'Plus tard',
      pt: 'Depois',
    },
    updateProblem: {
      it: 'Problema di aggiornamento',
      en: 'Update problem',
      es: 'Problema de actualización',
      de: 'Aktualisierungsproblem',
      fr: 'Problème de mise à jour',
      pt: 'Problema de atualização',
    },
    updateProblemDesc: {
      it: 'Troppi tentativi di aggiornamento.',
      en: 'Too many update attempts.',
      es: 'Demasiados intentos de actualización.',
      de: 'Zu viele Aktualisierungsversuche.',
      fr: 'Trop de tentatives de mise à jour.',
      pt: 'Muitas tentativas de atualização.',
    },
    reloadApp: {
      it: 'Ricarica App',
      en: 'Reload App',
      es: 'Recargar App',
      de: 'App neu laden',
      fr: 'Recharger',
      pt: 'Recarregar App',
    },
    clearCache: {
      it: 'Pulisci Cache e Ricarica',
      en: 'Clear Cache & Reload',
      es: 'Limpiar Caché y Recargar',
      de: 'Cache löschen & neu laden',
      fr: 'Vider le cache et recharger',
      pt: 'Limpar Cache e Recarregar',
    },
    closeTelegram: {
      it: 'Se il problema persiste, chiudi completamente Telegram e riapri l\'app.',
      en: 'If the problem persists, close Telegram completely and reopen the app.',
      es: 'Si el problema persiste, cierra Telegram completamente y reabre la app.',
      de: 'Wenn das Problem weiterhin besteht, schließe Telegram und öffne die App erneut.',
      fr: 'Si le problème persiste, fermez Telegram et rouvrez l\'appli.',
      pt: 'Se o problema persistir, feche o Telegram e reabra o app.',
    },
  };

  const entry = strings[key];
  if (!entry) return key;
  return entry[lang] || entry['en'] || key;
}

/**
 * Ottiene la versione corrente dal server
 */
async function fetchServerVersion(): Promise<VersionInfo | null> {
  try {
    // Aggiungi timestamp per evitare cache
    const response = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      console.warn('[VERSION] Failed to fetch version:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('[VERSION] Error fetching version:', error);
    return null;
  }
}

/**
 * Ottiene la versione salvata localmente
 */
function getStoredVersion(): string | null {
  return localStorage.getItem(VERSION_KEY);
}

/**
 * Salva la versione corrente
 */
function storeVersion(version: string): void {
  localStorage.setItem(VERSION_KEY, version);
  localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
}

/**
 * Controlla se abbiamo superato il limite di reload
 */
function checkReloadLimit(): boolean {
  const now = Date.now();
  const reloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0');
  const lastReloadTimestamp = parseInt(localStorage.getItem(RELOAD_TIMESTAMP_KEY) || '0');

  // Se è passato troppo tempo, resetta il contatore
  if (now - lastReloadTimestamp > RELOAD_WINDOW_MS) {
    localStorage.setItem(RELOAD_COUNT_KEY, '0');
    localStorage.setItem(RELOAD_TIMESTAMP_KEY, now.toString());
    return true;
  }

  // Se abbiamo superato il limite, blocca
  if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
    console.error('[VERSION] ⚠️ MAX RELOAD ATTEMPTS REACHED! Blocking further reloads.');
    return false;
  }

  // Incrementa contatore
  localStorage.setItem(RELOAD_COUNT_KEY, (reloadCount + 1).toString());
  localStorage.setItem(RELOAD_TIMESTAMP_KEY, now.toString());
  return true;
}

/**
 * Resetta il contatore di reload
 */
function resetReloadCounter(): void {
  localStorage.removeItem(RELOAD_COUNT_KEY);
  localStorage.removeItem(RELOAD_TIMESTAMP_KEY);
}

/**
 * Forza il reload completo dell'app con cache busting
 */
export function forceReload(): void {
  // PROTEZIONE LOOP: Controlla limite reload
  if (!checkReloadLimit()) {
    console.error('[VERSION] 🚫 Reload blocked to prevent infinite loop!');
    showLoopErrorMessage();
    return;
  }

  console.log('[VERSION] 🔄 Forcing full reload...');

  // Pulisci la cache
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        console.log('[VERSION] Deleting cache:', name);
        caches.delete(name);
      });
    });
  }

  // Costruisci URL con cache busting
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());

  // Reload forzato
  window.location.replace(url.toString());
}

/**
 * Mostra messaggio di errore quando il loop è rilevato
 */
function showLoopErrorMessage(): void {
  // Rimuovi eventuali messaggi precedenti
  const existing = document.getElementById('loop-error-message');
  if (existing) existing.remove();

  // Rimuovi anche il reloading message
  const reloading = document.getElementById('chunk-error-message');
  if (reloading) reloading.remove();

  const message = document.createElement('div');
  message.id = 'loop-error-message';
  message.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 9999999;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 30px;
      text-align: center;
    ">
      <div style="
        width: 80px;
        height: 80px;
        border-radius: 20px;
        background: rgba(239, 68, 68, 0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 24px;
      ">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <div style="font-size: 20px; font-weight: 700; margin-bottom: 10px;">
        ${t('updateProblem')}
      </div>
      <div style="font-size: 15px; opacity: 0.7; max-width: 300px; line-height: 1.5; margin-bottom: 28px;">
        ${t('updateProblemDesc')}
      </div>
      <div style="display: flex; flex-direction: column; gap: 12px; width: 100%; max-width: 280px;">
        <button onclick="window.location.reload(true)" style="
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 14px;
          font-weight: 600;
          cursor: pointer;
          font-size: 15px;
          letter-spacing: 0.3px;
        ">${t('reloadApp')}</button>
        <button onclick="window.forceHardReset()" style="
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.8);
          border: 1px solid rgba(255,255,255,0.12);
          padding: 14px 24px;
          border-radius: 14px;
          font-weight: 600;
          cursor: pointer;
          font-size: 15px;
        ">${t('clearCache')}</button>
      </div>
      <div style="margin-top: 28px; font-size: 12px; opacity: 0.4; max-width: 260px; line-height: 1.5;">
        ${t('closeTelegram')}
      </div>
    </div>
  `;

  document.body.appendChild(message);
}

/**
 * Reset completo e hard reload
 */
function forceHardReset(): void {
  console.log('[VERSION] 🧹 Hard reset requested');

  // Pulisci TUTTO
  localStorage.clear();
  sessionStorage.clear();

  // Pulisci cache
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }

  // Reload con timestamp
  window.location.href = '/?_reset=' + Date.now();
}

(window as any).forceHardReset = forceHardReset;

/**
 * Verifica se c'è una nuova versione disponibile
 */
export async function checkForUpdates(): Promise<boolean> {
  // ⭐ FIX: Se l'utente è nel flow di application boarding, NON forzare reload
  // per evitare che l'utente perda il progresso del form
  const inApplicationFlow = sessionStorage.getItem('in_application_flow');
  if (inApplicationFlow) {
    console.log('[VERSION] Skipping version check - user is in application boarding flow');
    return false;
  }

  // Se siamo in recovery mode dopo un loop, non controllare subito
  const reloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0');
  if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
    console.warn('[VERSION] Skipping version check - reload limit reached');
    return false;
  }

  // Evita check troppo frequenti
  const lastCheck = parseInt(localStorage.getItem(LAST_CHECK_KEY) || '0');
  const now = Date.now();

  if (now - lastCheck < 5000) { // Minimo 5 secondi tra i check
    return false;
  }

  const serverVersion = await fetchServerVersion();
  if (!serverVersion) {
    return false;
  }

  const storedVersion = getStoredVersion();

  // Prima installazione
  if (!storedVersion) {
    console.log('[VERSION] First install, storing version:', serverVersion.version);
    storeVersion(serverVersion.version);
    return false;
  }

  // Versione diversa = nuovo deploy
  if (serverVersion.version !== storedVersion &&
      serverVersion.version !== '__BUILD_TIMESTAMP__') {
    console.log('[VERSION] 🆕 New version detected!', {
      stored: storedVersion,
      server: serverVersion.version
    });
    // Store the new version BEFORE reloading to prevent reload loops
    storeVersion(serverVersion.version);
    return true;
  }

  return false;
}

/**
 * Esegue il check periodico delle versioni
 */
export function startVersionChecking(): void {
  console.log('[VERSION] Starting version checking...');

  // Se abbiamo superato il limite di reload, non avviare il check
  const reloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0');
  if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
    console.warn('[VERSION] Version checking disabled - reload limit reached');
    showLoopErrorMessage();
    return;
  }

  // Check immediato (con delay per lasciare l'app caricare)
  setTimeout(async () => {
    const hasUpdate = await checkForUpdates();
    if (hasUpdate) {
      // Consolidamento: prompt user-driven, NON auto-reload (l'auto-reload a
      // metà sessione era invasivo; la freschezza al load è già garantita da
      // SW + script inline in index.html).
      console.log('[VERSION] Update detected on initial check, prompting user…');
      showUpdateNotification();
    }
  }, 10000); // Aspetta 10 secondi prima del primo check

  // Check periodico
  setInterval(async () => {
    const hasUpdate = await checkForUpdates();
    if (hasUpdate) {
      console.log('[VERSION] Update detected on periodic check, prompting user…');
      showUpdateNotification();
    }
  }, VERSION_CHECK_INTERVAL);
}

/**
 * Mostra notifica di aggiornamento disponibile
 * Con margine top per evitare la status bar di Telegram
 */
function showUpdateNotification(): void {
  // Verifica se il banner esiste già
  if (document.getElementById('update-notification')) {
    return;
  }

  // Se abbiamo superato il limite di reload, non mostrare il banner
  const reloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0');
  if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
    console.warn('[VERSION] Not showing update banner - reload limit reached');
    showLoopErrorMessage();
    return;
  }

  // Crea elemento notifica
  const notification = document.createElement('div');
  notification.id = 'update-notification';
  notification.innerHTML = `
    <div id="update-backdrop" style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(6,8,12,0.6);
      z-index: 999998;
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
    "></div>
    <div style="
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #141416;
      color: #f5f5f5;
      padding: 28px 24px;
      text-align: center;
      z-index: 999999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 30px 70px -20px rgba(0,0,0,0.7);
      border: 1px solid rgba(255,255,255,0.14);
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 14px;
      min-width: 300px;
      max-width: 90vw;
      width: 360px;
    ">
      <img src="/assets/images/brand/icon-512.webp" alt="Kinkygram" width="64" height="64" style="
        width: 64px;
        height: 64px;
        border-radius: 15px;
        object-fit: cover;
        border: 1px solid rgba(255,255,255,0.10);
        box-shadow: 0 8px 22px -8px rgba(0,0,0,0.6);
      " />
      <div style="font-weight: 700; font-size: 18px; letter-spacing: -0.01em; color: #fff;">${t('newVersion')}</div>
      <div style="font-size: 13.5px; color: #cfcfd3; max-width: 260px; line-height: 1.5;">
        ${t('newVersionDesc')}
      </div>
      <button id="update-btn" style="
        background: linear-gradient(135deg, #ff6ba1 0%, #c98ad9 100%);
        color: white;
        border: none;
        padding: 13px 36px;
        border-radius: 12px;
        font-weight: 700;
        cursor: pointer;
        font-size: 15px;
        margin-top: 6px;
        width: 100%;
        max-width: 280px;
        box-shadow: 0 12px 30px -10px #ff6ba1;
      ">${t('updateNow')}</button>
      <button id="update-later-btn" style="
        background: transparent;
        color: #808088;
        border: none;
        padding: 6px 24px;
        font-weight: 500;
        cursor: pointer;
        font-size: 13px;
      ">${t('updateLater')}</button>
    </div>
  `;

  document.body.appendChild(notification);

  // Aggiungi event listeners
  const updateBtn = document.getElementById('update-btn');
  const laterBtn = document.getElementById('update-later-btn');

  if (updateBtn) {
    updateBtn.addEventListener('click', () => {
      forceReload();
    });
  }

  if (laterBtn) {
    laterBtn.addEventListener('click', () => {
      // Chiudi il modal
      notification.remove();
      // Postponi il prossimo check
      localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
    });
  }

  // Segna che il banner è stato mostrato (per recovery)
  localStorage.setItem('update_shown', Date.now().toString());
}

/**
 * Configura handler globale per errori di caricamento chunk
 */
export function setupChunkErrorHandler(): void {
  // Handler per errori di import dinamici
  window.addEventListener('error', (event) => {
    const errorMessage = event.error?.message || event.message || '';
    const isChunkError =
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Cannot find module');

    if (isChunkError) {
      console.error('[CHUNK ERROR] Detected chunk load error:', errorMessage);
      handleChunkError();
    }
  });

  // Handler per promise rejection (errori async)
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || event.reason || '';
    const isChunkError =
      typeof errorMessage === 'string' && (
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError') ||
        errorMessage.includes('Failed to fetch dynamically imported module')
      );

    if (isChunkError) {
      console.error('[CHUNK ERROR] Detected async chunk error:', errorMessage);
      event.preventDefault();
      handleChunkError();
    }
  });
}

/**
 * Gestisce l'errore di caricamento chunk
 */
let chunkErrorHandled = false;

function handleChunkError(): void {
  if (chunkErrorHandled) return;
  chunkErrorHandled = true;

  console.log('[CHUNK ERROR] Handling chunk error...');

  // Salva l'informazione dell'errore
  localStorage.setItem('last_chunk_error', Date.now().toString());

  // Controlla se abbiamo già provato a ricaricare recentemente
  const reloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0');

  // Se abbiamo superato il limite, mostra il messaggio di errore
  if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
    console.error('[CHUNK ERROR] Max reload attempts reached, showing error message');
    showLoopErrorMessage();
    return;
  }

  // Mostra messaggio all'utente
  showReloadingMessage();

  // Attendi e ricarica
  setTimeout(() => {
    forceReload();
  }, 1500);
}

/**
 * Mostra messaggio di ricaricamento — usa il nuovo overlay React (`UpdateOverlay`)
 * con i 4 stati hi-fi del design handoff. Il fallback DOM raw resta più sotto
 * per le pagine pubbliche / contesti in cui il React tree non è ancora montato.
 */
function showReloadingMessage(): void {
  try {
    setUpdateOverlayState({ kind: 'updating' });
    return;
  } catch (_) {
    // se per qualche motivo lo state non è disponibile, cadiamo sul fallback DOM
  }
  showReloadingMessageFallback();
}

function showReloadingMessageFallback(): void {
  const existing = document.getElementById('chunk-error-message');
  if (existing) return;

  const message = document.createElement('div');
  message.id = 'chunk-error-message';
  message.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 999999;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 20px;
      text-align: center;
    ">
      <div style="
        width: 80px;
        height: 80px;
        border-radius: 20px;
        background: rgba(102, 126, 234, 0.12);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 28px;
      ">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#667eea" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spinIcon 2s linear infinite;">
          <path d="M21 12a9 9 0 1 1-6.22-8.56"/>
          <polyline points="21 3 21 12 12 12"/>
        </svg>
      </div>
      <div style="font-size: 19px; font-weight: 700; margin-bottom: 8px; letter-spacing: -0.3px;">
        ${t('updating')}
      </div>
      <div style="font-size: 14px; opacity: 0.5; max-width: 260px; line-height: 1.5;">
        ${t('updatingDesc')}
      </div>
      <div style="margin-top: 32px; display: flex; gap: 6px;">
        <div style="width: 6px; height: 6px; border-radius: 50%; background: #667eea; animation: bounce 1.4s ease-in-out infinite;"></div>
        <div style="width: 6px; height: 6px; border-radius: 50%; background: #667eea; animation: bounce 1.4s ease-in-out 0.2s infinite;"></div>
        <div style="width: 6px; height: 6px; border-radius: 50%; background: #667eea; animation: bounce 1.4s ease-in-out 0.4s infinite;"></div>
      </div>
    </div>
    <style>
      @keyframes spinIcon { to { transform: rotate(360deg); } }
      @keyframes bounce {
        0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
        40% { transform: scale(1); opacity: 1; }
      }
    </style>
  `;

  document.body.appendChild(message);
}

/**
 * Esporta funzione globale per reload manuale
 */
(window as any).forceAppReload = forceReload;

/**
 * Inizializza tutto il sistema di version checking
 * Abilita check periodico con protezione anti-loop
 */
export function initVersionSystem(): void {
  console.log('[VERSION] Initializing version system...');

  // Setup handler per errori chunk
  setupChunkErrorHandler();

  // Reset contatore reload se l'app si è caricata correttamente
  setTimeout(() => {
    const reloadCount = parseInt(localStorage.getItem(RELOAD_COUNT_KEY) || '0');
    if (reloadCount > 0) {
      console.log('[VERSION] App loaded successfully, resetting reload counter');
      resetReloadCounter();
      localStorage.removeItem('last_chunk_error');
      localStorage.removeItem('update_shown');
    }
  }, 5000);

  // Abilita check periodico — cattura deploy che avvengono mentre l'app è aperta
  startVersionChecking();

  console.log('[VERSION] Version system initialized with periodic checking');
}

export default {
  initVersionSystem,
  checkForUpdates,
  forceReload,
  startVersionChecking,
  setupChunkErrorHandler
};
